/**
 * ClickPay Phase 4 — layout wrapper for the vendored MasterData ("ops")
 * pages mounted under /inventory/*.
 *
 * Scopes MasterData's app-level providers (Toast, keyboard shortcuts) to the
 * ops subtree ONLY, so AdBetter pages are untouched. (UIPrefs moved up to the
 * app shell in PR 12: one display-preferences store for every table.)
 * Also reproduces MasterData's <main> flex column (flex-1 overflow-hidden
 * flex flex-col) that its PageShell-based pages rely on for internal
 * scrolling, inside AdBetter's overflow-auto <main>.
 *
 * Port-as-is decision (owner-approved): no restyle here — restyle is
 * post-merge priority #1.
 */
import { Suspense, useEffect, type ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import { ToastProvider } from './components/Toast'
import { KeyboardShortcutProvider } from './lib/useKeyboardShortcuts'
import { loadApiKey, loadCurrentUser } from './lib/api'

function PageFallback() {
  return (
    <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
      Loading…
    </div>
  )
}

export default function OpsLayout({ children }: { children?: ReactNode } = {}) {
  // MasterData's App.tsx hydrated the API-key + current-user headers from
  // localStorage on mount; do the same when the ops subtree first renders.
  useEffect(() => {
    loadCurrentUser()
    loadApiKey()
  }, [])

  // Route usage renders the matched child via <Outlet>; the Ops Hub embeds a
  // single ops page directly (e.g. the Ops Health tab) by passing `children`.
  return (
    <ToastProvider>
      <KeyboardShortcutProvider>
        <div className="h-full overflow-hidden flex flex-col">
          <Suspense fallback={<PageFallback />}>
            {children ?? <Outlet />}
          </Suspense>
        </div>
      </KeyboardShortcutProvider>
    </ToastProvider>
  )
}
